// BrandLogo (009 revision 6, FR-045; revision 11, R-19): the one way to show the ARCEL mark (the "a" tile) in every
// app, drawn from the one SVG
// source `src/brand/logo.svg` (the favicons of both apps are built from the same file by scripts/favicons.mjs; the
// bundler inlines the file into the page, so it costs no request). Sizes come from the system: `sm` 24 px and `md`
// 32 px in headers and footers, `lg` 64 px on the splash and sign-in. It is decorative unless it has a label (a logo
// inside a link that names the product takes none).
import { cn } from '../lib/cn';
import { SafeImage } from './SafeImage';

/** The logo file's address (the bundler turns it into a data URL or an asset). */
export const logoUrl = new URL('../brand/logo.svg', import.meta.url).href;

/** Props of {@link BrandLogo}. */
export interface BrandLogoProps {
  /** `sm` 24 px, `md` 32 px (default), `lg` 64 px. */
  size?: 'sm' | 'md' | 'lg';
  /** The accessible name (the product's name); without one the logo is hidden from assistive technology. */
  label?: string;
  className?: string;
}

const sizes = { sm: 'size-6', md: 'size-8', lg: 'size-16' };

/**
 * The logo.
 * @param props size, accessible label and classes
 * @returns the logo
 */
export function BrandLogo({ size = 'md', label, className }: BrandLogoProps) {
  return (
    <span
      data-slot="brand-logo"
      data-size={size}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className={cn('inline-block shrink-0 overflow-hidden rounded-lg', sizes[size], className)}
    >
      <SafeImage src={logoUrl} className="block size-full" loading="eager" />
    </span>
  );
}
